import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface AuditLogEntry {
  entry_id: string;
  action: string;
  category: string;
  table_name?: string;
  record_id?: string;
  record_identifier?: string;
  old_value?: Record<string, unknown>;
  new_value?: Record<string, unknown>;
  changed_fields?: string[];
  user_id?: string;
  user_name?: string;
  ip_address?: string;
  created_at: string;
}

export interface AuditLogFilters {
  action?: string;
  category?: string;
  table_name?: string;
  user_id?: string;
  record_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface AuditSummary {
  total_entries_today: number;
  total_entries_week: number;
  entries_by_action: Record<string, number>;
  entries_by_category: Record<string, number>;
  recent_users: Array<{ user_id: string; action_count: number }>;
}

export interface RecordHistory {
  table_name: string;
  record_id: string;
  total_changes: number;
  history: Array<{
    entry_id: string;
    action: string;
    changed_fields?: string[];
    old_value?: Record<string, unknown>;
    new_value?: Record<string, unknown>;
    user_id?: string;
    ip_address?: string;
    created_at: string;
  }>;
}

export function useAuditLog() {
  const queryClient = useQueryClient();

  const getAuditLogs = (params?: {
    action?: string;
    category?: string;
    table_name?: string;
    user_id?: string;
    record_id?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }) =>
    useQuery<{ entries: AuditLogEntry[]; total: number; page: number; page_size: number }>({
      queryKey: ['audit-log', params],
      queryFn: () => api.get('/audit-log', { params }).then((res) => res.data),
    });

  const getRecordHistory = (tableName: string, recordId: string) =>
    useQuery<RecordHistory>({
      queryKey: ['record-history', tableName, recordId],
      queryFn: () => api.get(`/audit-log/record/${tableName}/${recordId}`).then((res) => res.data),
      enabled: !!tableName && !!recordId,
    });

  const getUserActivity = (
    userId: string,
    params?: { start_date?: string; end_date?: string; page?: number; page_size?: number }
  ) =>
    useQuery<{ user_id: string; total: number; page: number; page_size: number; entries: AuditLogEntry[] }>({
      queryKey: ['user-activity', userId, params],
      queryFn: () => api.get(`/audit-log/user/${userId}`, { params }).then((res) => res.data),
      enabled: !!userId,
    });

  const getSummary = () =>
    useQuery<AuditSummary>({
      queryKey: ['audit-summary'],
      queryFn: () => api.get('/audit-log/summary').then((res) => res.data),
    });

  const getAvailableActions = () =>
    useQuery<{
      actions: Array<{ value: string; label: string; description: string }>;
      categories: Array<{ value: string; label: string; description: string }>;
    }>({
      queryKey: ['audit-actions'],
      queryFn: () => api.get('/audit-log/actions').then((res) => res.data),
    });

  const exportAuditLogs = () =>
    useMutation({
      mutationFn: ({
        filters,
        format,
      }: {
        filters: AuditLogFilters;
        format: 'json' | 'csv';
      }) => api.post('/audit-log/export', filters, { params: { format } }),
    });

  return {
    getAuditLogs,
    getRecordHistory,
    getUserActivity,
    getSummary,
    getAvailableActions,
    exportAuditLogs,
  };
}
